import React from "react";
import { Shield, Lock, Eye, Database, UserCheck, Bell, FileText, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#002FA7] mb-8" data-testid="privacy-back-link">
          <ArrowLeft size={16} /> Back to Home
        </Link>

        <div className="bg-white border-2 border-black p-8 sm:p-12" data-testid="privacy-policy-page">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center">
              <Shield size={24} />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }}>Privacy Policy</h1>
              <p className="text-sm text-gray-500 mt-1">Last updated: February 1, 2026</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                <Eye size={18} /> 1. Overview
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                MOSAIC ("Multilingual Outcomes & Student Assessment for Instructional Clarity") is operated by Lerno Co. This Privacy Policy describes how we collect, use, store, and protect personally identifiable information (PII) of students, parents, teachers, and school administrators who use the MOSAIC platform. MOSAIC is designed exclusively for educational purposes and complies with applicable federal and state privacy laws.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                <FileText size={18} /> 2. Legal Compliance
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">MOSAIC is designed and operated in compliance with:</p>
              <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                <li><strong>FERPA</strong> — Family Educational Rights and Privacy Act (20 U.S.C. &sect; 1232g)</li>
                <li><strong>COPPA</strong> — Children's Online Privacy Protection Act (15 U.S.C. &sect;&sect; 6501-6506)</li>
                <li><strong>NYS Education Law &sect; 2-d</strong> — Student Data Privacy and Security</li>
                <li><strong>NYCPS Parents' Bill of Rights</strong> for Data Privacy and Security</li>
                <li><strong>NIST Cybersecurity Framework v2.0</strong> — Security safeguard alignment</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                <Database size={18} /> 3. Data We Collect
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">We collect only data necessary for educational assessment purposes:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-3 border-b font-semibold">Data Element</th>
                      <th className="text-left p-3 border-b font-semibold">Purpose</th>
                      <th className="text-left p-3 border-b font-semibold">Shared Externally?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Student Name & Email", "Identity, authentication", "No"],
                      ["Grade Level", "Grade-appropriate assessment", "No"],
                      ["School / District", "Multi-tenant data isolation", "No"],
                      ["Assessment Responses", "Scoring, growth tracking", "No"],
                      ["Audio Recordings (Speaking)", "Speech evaluation", "Yes — the MOSAIC content-scoring vendor (processing only)"],
                      ["Teacher Name & Email", "Class management", "No"],
                      ["Parent Name & Email", "Portal access", "No"],
                      ["IP Address", "Audit logging, security", "No"],
                    ].map(([element, purpose, shared], i) => (
                      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="p-3 border-b">{element}</td>
                        <td className="p-3 border-b">{purpose}</td>
                        <td className="p-3 border-b">{shared}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                <Lock size={18} /> 4. How We Protect Data
              </h2>
              <ul className="list-disc pl-6 text-sm text-gray-700 space-y-2">
                <li><strong>Encryption in Transit:</strong> All data is transmitted via TLS 1.2+ (HTTPS)</li>
                <li><strong>Encryption at Rest:</strong> AES-256 encryption on MongoDB Atlas storage</li>
                <li><strong>Access Control:</strong> 5-tier Role-Based Access Control (Student, Teacher, School Admin, District Admin, Super Admin)</li>
                <li><strong>Audit Logging:</strong> All security-relevant actions (login, grading, data access, impersonation) are logged with timestamps and IP addresses</li>
                <li><strong>Session Security:</strong> HTTP-only, secure cookies with automatic expiration (24-hour TTL)</li>
                <li><strong>Password Security:</strong> bcrypt hashing with salting</li>
                <li><strong>Anti-Cheating:</strong> Tab-switch detection and exam lockdown during active assessments</li>
                <li><strong>Security Headers:</strong> HSTS, CSP, X-Frame-Options (DENY), X-Content-Type-Options</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                <UserCheck size={18} /> 5. COPPA &amp; Parental Consent
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                For students under 13 (typically grades K-5), MOSAIC requires documented parental consent before assessment data is collected. Schools are responsible for obtaining and recording consent, which is tracked within the platform's COPPA consent management system. No data from students under 13 is shared with third parties for non-educational purposes.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                6. Data Retention &amp; Deletion
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                Student assessment records are retained for 7 years per NYS record retention guidelines, or as configured by the district. Upon request, individual student data can be permanently deleted from all MOSAIC systems. Disabled user accounts are automatically purged after a configurable grace period (default: 90 days). Districts may request bulk data deletion upon contract termination.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                7. Parental Rights
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                In accordance with FERPA and NYS Education Law &sect; 2-d, parents/guardians have the right to:
              </p>
              <ul className="list-disc pl-6 text-sm text-gray-700 space-y-1">
                <li>Inspect all data held about their child via the Parent Portal data export</li>
                <li>Request correction of inaccurate records</li>
                <li>Request deletion of their child's data</li>
                <li>File a complaint with the school, NYSED, or the U.S. Department of Education</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                8. No Secondary Use
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                MOSAIC does <strong>not</strong> use student data for advertising, profiling, behavioral targeting, or any purpose beyond educational assessment. Data is never sold, rented, or shared for commercial purposes.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                <Bell size={18} /> 9. Breach Notification
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                In the event of a data breach affecting PII, Lerno Co. will notify affected schools and NYCPS within <strong>24 hours</strong> of discovery, in compliance with FERPA and NYS Education Law &sect; 2-d requirements. Breach reports are maintained in the platform's compliance audit trail.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                10. Third-Party Subprocessors
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                MOSAIC uses a limited number of third-party services to operate. All subprocessors have signed Data Processing Agreements (DPAs) with equivalent privacy protections. See our <Link to="/subprocessors" className="text-[#002FA7] underline">Subprocessor List</Link> for details.
              </p>
            </section>

            <section>
              <h2 className="text-lg font-bold flex items-center gap-2 border-b-2 border-gray-200 pb-2 mb-4">
                11. Contact
              </h2>
              <p className="text-sm text-gray-700 leading-relaxed">
                For privacy inquiries, data access requests, or to report a concern:<br />
                <strong>Lerno Co. — Privacy Office</strong><br />
                Email: privacy@mosaicassessmentco.com<br />
                Web: <a href="https://mosaicassessmentco.com" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] underline">mosaicassessmentco.com</a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
